<template>
  <modal-wrapper
    ref="modalRef"
    :close-on-ok-click="false"
    :content-overflow-y="'visible'"
    :header="t('workspaceDocuments.selectContentTypeTitle')"
    :ok-button-label="t('generic.select')"
    @ok-button-click="handleOk"
    @cancel-button-click="handleCancel"
    @hide="handleCancel"
    @show="onModalShow"
  >
    <div class="select-container">
      <styled-multiselect
        v-model="selectedContentType"
        :options="contentTypes"
        :placeholder="t('workspaceDocuments.selectContentTypePlaceholder')"
        label="name"
        track-by="name"
        :loading="isLoadingContentTypes"
        :is-error="isSubmitted && !selectedContentType"
      />
      <message-wrapper v-if="errorMessage" simple-error>
        {{ errorMessage }}
      </message-wrapper>
    </div>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import axios, { AxiosResponse } from 'axios';
  import { useI18n } from 'vue-i18n';
  import { IContentTypeNameAndId } from 'types';
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import { endpointsList } from '@/helpers';
  import { useToast } from '@/composables/useToast';
  import { useModalWrapper } from '@/components/ModalWrapper';
  import MessageWrapper from '@/components/MessageWrapper/MessageWrapper.vue';

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { modalRef, show, hide } = useModalWrapper();

  defineExpose({ show, hide });

  const emit = defineEmits<{
    (e: 'content-type-selected', contentType: IContentTypeNameAndId): void;
  }>();

  const contentTypes = ref<IContentTypeNameAndId[]>([]);
  const selectedContentType = ref<IContentTypeNameAndId | null>(null);
  const isLoadingContentTypes = ref(false);
  const errorMessage = ref<string | null>(null);
  const isSubmitted = ref(false);

  function resetState() {
    selectedContentType.value = null;
    errorMessage.value = null;
    isSubmitted.value = false;
  }

  function handleCancel(): void {
    resetState();
  }

  function handleOk(): void {
    isSubmitted.value = true;
    errorMessage.value = null;

    if (!selectedContentType.value) {
      errorMessage.value = t('workspaceDocuments.errors.noContentTypeSelected');
      return;
    }

    emit('content-type-selected', selectedContentType.value);
    hide();
    resetState();
  }

  async function onModalShow(): Promise<void> {
    resetState();
    isLoadingContentTypes.value = true;
    try {
      const response: AxiosResponse<IContentTypeNameAndId[]> = await axios.get(
        endpointsList.contentTypeMappings.getAvailableContentTypes,
      );
      contentTypes.value = response.data;
    } catch {
      createErrorToast(t('errors.error'));
    } finally {
      isLoadingContentTypes.value = false;
    }
  }
</script>

<style scoped lang="scss">
  .select-container {
    display: flex;
    flex-direction: column;
    row-gap: 12px;
  }
</style>
